import { useGTMConsentCookie } from "./useGTMConsentCookie";
import { getDefaultNewCookieExpiration } from "./helpers";
import { useCookies } from "react-cookie";
import { useCCPAEnabledCrossDomainCookie } from "./useCCPAEnabledCrossDomainCookie";
import { useCountryStateCookies } from "./useCountryStateCookies";
import { useCallback, useEffect, useMemo } from "react";
import { INITIAL_COOKIES } from "./constants";
import { z } from "zod";
import type { CookieSettings, RegionCookieSettings } from "./types";
import { regionCookieSettingsSchema } from "./types";

const COOKIE_SETTINGS_COOKIE_NAME = "cookieSettingsV2";

const cookieSettingsParserSchema = z
  .string()
  .transform((str, ctx) => {
    try {
      return JSON.parse(str);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid JSON string"
      });
      return z.NEVER;
    }
  })
  .pipe(regionCookieSettingsSchema);

export const useCookieSettings = () => {
  const { isWithinGDPR, country, state } = useCountryStateCookies();
  const cookieRegion = `${country}-${state}`;
  const [cookies, setCookie] = useCookies([COOKIE_SETTINGS_COOKIE_NAME]);
  const { set: setGTMConsentCookie } = useGTMConsentCookie();
  const { set: setCCPAEnabledCrossDomainCookie } =
    useCCPAEnabledCrossDomainCookie();

  const rawCookieValue = cookies[COOKIE_SETTINGS_COOKIE_NAME];

  // Default cookie settings based on region
  const conditionalInitialCookies = useMemo<RegionCookieSettings>(
    () => ({
      ...INITIAL_COOKIES,
      // Default to analytics disabled within GDPR
      allowAnalytics: !isWithinGDPR,
      _settingsEffectiveRegion: cookieRegion
    }),
    [isWithinGDPR, cookieRegion]
  );

  // Parse and validate cookie settings
  const currentSettings = useMemo<RegionCookieSettings>(() => {
    if (!rawCookieValue) {
      return conditionalInitialCookies;
    }

    if (typeof rawCookieValue === "string") {
      const result = cookieSettingsParserSchema.safeParse(rawCookieValue);
      if (result.success) {
        return result.data;
      } else {
        return conditionalInitialCookies;
      }
    }

    // If it's already an object (not a string), validate it with the schema
    const result = regionCookieSettingsSchema.safeParse(rawCookieValue);
    return result.success ? result.data : conditionalInitialCookies;
  }, [rawCookieValue, conditionalInitialCookies]);

  // Update cookie settings and sync with related cookies
  const setCookieSettings = useCallback(
    (partialCS: Partial<CookieSettings>) => {
      const updatedSettings = {
        ...currentSettings,
        ...partialCS
      };

      // Set domain for gemini.com and subdomains
      const hostname = window.location.hostname;
      const shouldSetDomain =
        hostname === "gemini.com" || hostname.endsWith(".gemini.com");

      setCookie(COOKIE_SETTINGS_COOKIE_NAME, JSON.stringify(updatedSettings), {
        ...(shouldSetDomain ? { domain: ".gemini.com" } : {}),
        expires: getDefaultNewCookieExpiration(),
        path: "/"
      });

      // Whenever cookieSettings cookie is saved, update Google Tag Manager cookie
      // Allows GTM to know to enable/disable pageview tracking tags
      setGTMConsentCookie(updatedSettings);
      setCCPAEnabledCrossDomainCookie(updatedSettings);
    },
    [
      currentSettings,
      setCookie,
      setGTMConsentCookie,
      setCCPAEnabledCrossDomainCookie
    ]
  );

  // Reset cookie settings if user changes region
  useEffect(() => {
    if (cookieRegion !== currentSettings._settingsEffectiveRegion) {
      setCookieSettings(conditionalInitialCookies);
    }
  }, [
    cookieRegion,
    currentSettings._settingsEffectiveRegion,
    conditionalInitialCookies,
    setCookieSettings
  ]);

  return {
    cookieSettings: currentSettings,
    setCookieSettings
  } as const;
};
